/* © NXAPP Limited */
import { useState, Suspense, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { useAuthContext } from '@/context/AuthContext'
import { Save, ArrowLeft, Copy, Check, Clock } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import { PLAIN_STARTER_HTML } from '@/components/builder/plainStarter'

const EmailBuilder = lazyWithReload(() => import('@/components/builder/EmailBuilder'))
const SimpleEmailEditor = lazyWithReload(() => import('@/components/builder/SimpleEmailEditor'))

const SIMPLE_MODE = { mode: 'simple' } as const
const isSimple = (d: Record<string, unknown> | undefined) => d?.mode === 'simple'

const schema = z.object({ name: z.string().min(1, 'Name is required') })
type Form = z.infer<typeof schema>

type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export default function TemplateEditorPage() {
  // campaignId is present when accessed via /campaigns/:campaignId/templates/...
  const { id, campaignId } = useParams<{ id?: string; campaignId?: string }>()
  const isNew = !id
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Back destination depends on context
  const backPath = campaignId ? `/campaigns/${campaignId}` : '/templates'

  const [html, setHtml] = useState('')
  const [design, setDesign] = useState<Record<string, unknown>>({})
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle')
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentIdRef = useRef<string | undefined>(id)

  // Keep ref in sync when navigating new→existing after first save
  useEffect(() => { currentIdRef.current = id }, [id])

  const { isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: async () => {
      const { data } = await supabase.from('templates').select('*').eq('id', id!).single()
      if (data) { setHtml(data.html); setDesign(data.design_json ?? {}) }
      return data
    },
    enabled: !isNew,
  })

  const { register, handleSubmit, getValues, formState: { errors, isSubmitting } } = useForm<Form>({
    resolver: zodResolver(schema),
  })

  // ─── Manual save ────────────────────────────────────────────────────────────
  const save = useMutation({
    mutationFn: async (data: Form) => {
      // campaign_id: null = shared; campaignId from URL = campaign-specific
      const payload = {
        name: data.name, html, design_json: design, user_id: user!.id,
        campaign_id: campaignId ?? null,
      }
      if (isNew) {
        const { data: created, error } = await supabase.from('templates').insert(payload).select().single()
        if (error) throw error
        return created
      } else {
        const { error } = await supabase.from('templates').update(payload).eq('id', id!)
        if (error) throw error
      }
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      if (isNew && created) {
        const editPath = campaignId
          ? `/campaigns/${campaignId}/templates/${created.id}/edit`
          : `/templates/${created.id}/edit`
        navigate(editPath, { replace: true })
      }
      setAutosaveStatus('saved')
      setTimeout(() => setAutosaveStatus('idle'), 2500)
    },
  })

  // ─── Autosave (existing templates only) ─────────────────────────────────────
  const autosave = useCallback(async (currentHtml: string, currentDesign: Record<string, unknown>) => {
    const tid = currentIdRef.current
    if (!tid || !user) return
    setAutosaveStatus('saving')
    const name = getValues('name') || 'Untitled'
    const { error } = await supabase
      .from('templates')
      .update({ html: currentHtml, design_json: currentDesign, name })
      .eq('id', tid)
      .eq('user_id', user.id)
    setAutosaveStatus(error ? 'error' : 'saved')
    if (!error) setTimeout(() => setAutosaveStatus('idle'), 2500)
  }, [user, getValues])

  const handleEditorChange = useCallback((h: string, d: Record<string, unknown>) => {
    setHtml(h)
    setDesign(d)
    if (currentIdRef.current) {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
      autosaveTimer.current = setTimeout(() => autosave(h, d), 4000)
    }
  }, [autosave])

  // ─── Save a Copy ────────────────────────────────────────────────────────────
  const saveACopy = useMutation({
    mutationFn: async () => {
      const name = `${getValues('name') || 'Template'} (Copy)`
      const { data, error } = await supabase
        .from('templates')
        .insert({ user_id: user!.id, name, html, design_json: design })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      const copyPath = campaignId
        ? `/campaigns/${campaignId}/templates/${created.id}/edit`
        : `/templates/${created.id}/edit`
      navigate(copyPath, { replace: false })
    },
  })

  if (!isNew && isLoading) return <div className="flex justify-center py-20"><Spinner /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(backPath)} className="btn-secondary p-2">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{isNew ? 'New template' : 'Edit template'}</h1>
          {/* Autosave indicator */}
          {!isNew && autosaveStatus !== 'idle' && (
            <p className={`text-xs mt-0.5 flex items-center gap-1 ${
              autosaveStatus === 'error' ? 'text-red-500' :
              autosaveStatus === 'saving' ? 'text-gray-400' : 'text-green-600'
            }`}>
              {autosaveStatus === 'saving' && <><Clock className="w-3 h-3 animate-spin" /> Saving…</>}
              {autosaveStatus === 'saved' && <><Check className="w-3 h-3" /> Autosaved</>}
              {autosaveStatus === 'error' && 'Autosave failed — save manually'}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(d => save.mutate(d))} className="space-y-6">
        <div className="card p-4 flex items-end gap-4">
          <div className="flex-1">
            <label className="label">Template name *</label>
            <input className="input max-w-sm" placeholder="Weekly newsletter base" {...register('name')} />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>

          <div className="flex gap-2 shrink-0">
            {!isNew && (
              <button
                type="button"
                onClick={() => saveACopy.mutate()}
                disabled={saveACopy.isPending}
                className="btn-secondary"
                title="Duplicate this template"
              >
                <Copy className="w-4 h-4" />
                {saveACopy.isPending ? 'Copying…' : 'Save a Copy'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || save.isPending}
              className="btn-primary"
            >
              <Save className="w-4 h-4" />
              {(isSubmitting || save.isPending) ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Editor mode — plain templates skip the designer entirely */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Editor:</span>
          <div className="inline-flex bg-white border border-gray-200 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => {
                setDesign({ ...SIMPLE_MODE })
                if (!html.trim()) setHtml(PLAIN_STARTER_HTML)
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isSimple(design) ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
            >
              Plain
            </button>
            <button
              type="button"
              onClick={() => setDesign(d => (isSimple(d) ? {} : d))}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !isSimple(design) ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
            >
              Designer
            </button>
          </div>
        </div>

        <Suspense fallback={<div className="flex justify-center py-20 border border-gray-200 rounded-lg"><Spinner /></div>}>
          {isSimple(design) ? (
            <SimpleEmailEditor
              key="simple"
              initialHtml={html}
              onChange={h => handleEditorChange(h, { ...SIMPLE_MODE })}
            />
          ) : (
            <EmailBuilder
              key="designer"
              initialHtml={html}
              initialDesign={Object.keys(design).length ? design : undefined}
              onChange={handleEditorChange}
            />
          )}
        </Suspense>
      </form>
    </div>
  )
}
